/**
 * What is behind the chevron of one draft line: why I2Q picked what it picked, and the
 * rest of the line's facts. Closed by default; "Show details" in the draft caption opens
 * every row (remembered in this browser).
 *
 * In order: the shortlist ("Did you mean"), the reason sentence, the evidence, where the
 * line was read from (file and page, a scan or a photo), the quantity with "Correct", the
 * price block (band, margin, cost for a viewer who may see the margin), the quotation
 * history, the CRM marker, the rule reasons on the line, who decided, the feedback
 * control, "No item" / "Undo" and, behind a price a person set, "Undo price". Nothing
 * here changes what I2Q proposed.
 */

import {
  CircleDollarSign,
  CircleSlash,
  GitCompareArrows,
  History,
  Icon,
  type IconComponent,
  ListChecks,
  Mail,
  RefreshCw,
  Search,
  Tag,
  Target,
  Undo2,
} from "@norican/icons";
import { Alert, Badge, Button, DescriptionList, Typography } from "@norican/react";

import { LinePriceBlock, LineSourceLink } from "@/features/case-review/case-documents";
import { CrmLineMarker } from "@/features/case-review/crm-machines-panel";
import { LineProposals, ProvisionalBlock } from "@/features/case-review/investigation-panel";
import {
  CorrectionBadge,
  isAddedLine,
  overrideSentence,
  QtyCell,
} from "@/features/case-review/line-corrections";
import { DecisionNote, LineFeedback } from "@/features/case-review/line-decision";
import { lineStatusFor } from "@/features/case-review/line-semantics";
import { MatchingSources } from "@/features/case-review/matching-evidence";
import { LineQuoteHistory } from "@/features/case-review/quote-history";
import { LineRuleFlags } from "@/features/case-review/rule-flags-panel";
import { type CaseWrites } from "@/features/case-review/use-case-writes";
import { ReasonWhyLink } from "@/features/rules";
import { useAreaLabels } from "@/lib/areas";
import { isScanSource } from "@/lib/data";
import { type LineStatus, type QuoteCaseDetail, type QuoteLine } from "@/lib/mock";
import { lineReasons } from "@/lib/rules";
import {
  ACTION_LABELS,
  quotationLineChip,
  quoteDeltaText,
  reasonLabel,
  standInWord,
} from "@/lib/sales-flow";
import { customerOpenSentence, isCustomerOpen, PHOTO_LINE_TAG } from "@/lib/work-labels";

const EVIDENCE_ICON: Record<string, IconComponent> = {
  supersession: RefreshCw,
  "has-bought": History,
  separation: GitCompareArrows,
  "l0-band": Target,
  candidates: ListChecks,
  retrieval: Search,
  "no-price": CircleDollarSign,
  price: CircleDollarSign,
  // Decision D1 (19 Oct 2026): a colleague's reply in the thread names this number.
  colleague_reply: Mail,
};

function evidenceIcon(kind: string): IconComponent {
  return EVIDENCE_ICON[kind] ?? Tag;
}

/** "learned_correction" → "Learned correction". */
export function evidenceLabel(kind: string): string {
  const words = kind.replace(/[_-]/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "";
}

/** The items I2Q offers for a line a person has to decide; none for a matched line. */
export function suggestionsOf(
  line: QuoteLine,
  status: LineStatus,
): { item: string; name: string }[] {
  if (line.requestAction === "remove") {
    return [];
  }
  if (status !== "open" && status !== "suggested") {
    return [];
  }
  const offered = (line.suggestions ?? []).map((s) => ({ item: s.item, name: s.name ?? "" }));
  if (offered.length > 0) {
    return offered;
  }
  return status === "open" ? (line.candidates ?? []).map((item) => ({ item, name: "" })) : [];
}

/** The catalogue a matched item was found in, and whether it is flagged as a spare part. */
function CatalogueCaption({ line, caseArea }: { line: QuoteLine; caseArea: string | null }) {
  const areas = useAreaLabels();
  const area = line.catalogueArea;
  if (!area && line.isSpare !== false) {
    return null;
  }
  const otherArea = Boolean(area && caseArea && area !== caseArea);
  return (
    <span className="flex flex-col text-xs">
      {area && (
        <span className={otherArea ? "text-warning-fg" : "text-fg-subtle"}>
          {areas.label(area)} catalogue{otherArea ? ", not this case's area" : ""}
        </span>
      )}
      {line.isSpare === false && <span className="text-fg-subtle">not flagged as spare part</span>}
    </span>
  );
}

export interface DraftLineDetailsProps {
  detail: QuoteCaseDetail;
  line: QuoteLine;
  /** Position of the line in the stored draft (what the API expects). */
  lineIndex: number;
  caseArea: string | null;
  maySeeMargin: boolean;
  /** A customer is suggested but not confirmed: a line price waits for it. */
  customerSuggested: boolean;
  /** sim360 P33: a local price another company sets is greyed out. */
  priceNote: { greyed: boolean; text: string } | null;
  writes: CaseWrites;
  /** The viewer may approve a price below the limit: the held reason gets "Approve price". */
  mayApprove?: boolean;
}

export function DraftLineDetails({
  detail,
  line,
  lineIndex,
  caseArea,
  maySeeMargin,
  customerSuggested,
  priceNote,
  writes,
  mayApprove = false,
}: DraftLineDetailsProps) {
  const status = lineStatusFor(line);
  const resolution = line.resolution;
  const reason = resolution.reason?.trim() ?? "";
  const offered =
    isCustomerOpen(line.reasonCode) && line.matchedItemId ? [] : suggestionsOf(line, status);
  const evidence = resolution.evidence ?? [];
  const picked = line.decision?.kind === "item";
  const delta = quoteDeltaText(line);
  const canDecide = Boolean(line.lineKey) && !isAddedLine(line) && lineIndex >= 0;
  const open = (detail.workStatus ?? "open") === "open";

  return (
    <div className="flex min-w-0 flex-col gap-2 pt-2" data-line-details={line.id}>
      {/* What the deeper search proposes for this line, with its lookups and the two
          answers (round 2). An alternative changes nothing until a person picks it. */}
      <LineProposals detail={detail} line={line} lineIndex={lineIndex} writes={writes} />
      {offered.length > 0 && (
        <div className="flex flex-col gap-0.5">
          <Typography variant="caption" tone="subtle">
            Did you mean
          </Typography>
          <ul className="flex flex-col gap-0.5">
            {offered.map((suggestion) => (
              <li key={suggestion.item} className="text-fg text-xs break-words">
                <span className="font-mono font-bold">{suggestion.item}</span>
                {suggestion.name ? ` ${suggestion.name}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}
      {isCustomerOpen(line.reasonCode) && !line.decision && (
        <Typography variant="caption" tone="muted" className="break-words">
          {customerOpenSentence(line)}
        </Typography>
      )}
      {/* The real reason of a line without a fitting item (hunt round 2, Q-28 a): the
          label names it, the server's sentence under it says it in full. */}
      {reasonLabel(line.reasonCode) && (
        <Badge tone="warning" variant="outline" size="xs" className="w-fit" data-reason-label>
          {reasonLabel(line.reasonCode)}
        </Badge>
      )}
      {reason && (
        <Typography variant="caption" tone="muted" className="break-words">
          {reason}
        </Typography>
      )}
      {evidence.length > 0 && (
        <DescriptionList
          orientation="horizontal"
          items={evidence.map((item) => ({
            label: (
              <span className="text-fg-subtle inline-flex items-center gap-1.5">
                <Icon icon={evidenceIcon(item.kind)} size="xs" />
                {evidenceLabel(item.kind)}
              </span>
            ),
            value: item.detail,
          }))}
        />
      )}
      {picked
        ? line.decision?.item_area && (
            <span className="text-fg-subtle text-xs">
              {line.decision.item_area.toUpperCase()} catalogue
            </span>
          )
        : resolution.item_id && <CatalogueCaption line={line} caseArea={caseArea} />}
      {/* Where the match was taken from (a sheet and row of an attachment): behind the
          chevron with the other sources, not on the row. */}
      <MatchingSources sources={line.sourceEvidence} />
      <div className="flex flex-wrap items-center gap-1">
        <LineSourceLink caseId={detail.id} source={line.source} />
        {line.customerItem && (
          <span className="text-fg-subtle text-xs break-all">
            Customer item number {line.customerItem}
          </span>
        )}
        {line.fromPartsList && (
          <Badge tone="neutral" variant="outline" size="xs">
            {standInWord(line.source?.kind === "attachment" ? line.source.doc_type : null)}
          </Badge>
        )}
        {line.source?.kind === "quotation" && (
          <Badge tone="neutral" variant="outline" size="xs" data-quotation-line="">
            {quotationLineChip(line.source.doc_number)}
          </Badge>
        )}
        {delta && (
          <Badge
            tone={line.quoteDelta === "remove" ? "warning" : "info"}
            variant="outline"
            size="xs"
          >
            {delta}
          </Badge>
        )}
        {isScanSource(line.source) && (
          <>
            <Badge
              tone="warning"
              variant="outline"
              size="xs"
              data-line-provenance={line.fromPhoto ? "photo" : "scan"}
            >
              {line.fromPhoto ? PHOTO_LINE_TAG : "From a scan"}
            </Badge>
            <ReasonWhyLink reasonCode={line.fromPhoto ? "photo_read" : "scan_read"} />
          </>
        )}
      </div>
      {line.qtyIssue && (
        <Alert tone="warning" data-qty-issue="">
          {line.qtyIssue}
        </Alert>
      )}
      {!line.qtyIssue && line.qtyConversion && (
        <Typography variant="caption" tone="muted" className="break-words">
          Converted: {line.qtyConversion}
        </Typography>
      )}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-fg-subtle">Quantity</span>
        <QtyCell caseId={detail.id} line={line} lineIndex={lineIndex} />
      </div>
      <LinePriceBlock
        price={line.price}
        showCost={maySeeMargin}
        customerSuggested={customerSuggested}
        note={priceNote}
        override={line.override}
      />
      {/* Provisional values for the suggested account: only while the line has no price
          to quote of its own. */}
      {line.price?.status !== "found" && <ProvisionalBlock provisional={line.provisional} />}
      <LineQuoteHistory history={line.quoteHistory} price={line.price} />
      <CrmLineMarker caseId={detail.id} lineIndex={lineIndex} />
      <LineRuleFlags
        reasons={lineReasons(detail.rules ?? null, line.lineKey)}
        onApprovePrice={
          mayApprove && open && lineIndex >= 0
            ? () => writes.approvePrice(lineIndex, true)
            : undefined
        }
        busy={writes.busy}
      />
      <DecisionNote line={line} />
      <CorrectionBadge override={line.override} />
      {overrideSentence(line.override) && (
        <Typography variant="caption" tone="muted" data-override-note>
          {overrideSentence(line.override)}
        </Typography>
      )}
      {canDecide && line.lineKey && (
        <div className="flex flex-wrap items-center gap-2">
          {line.decision ? (
            <Button
              size="sm"
              variant="ghost"
              tone="neutral"
              disabled={writes.busy || !open}
              startIcon={<Icon icon={Undo2} size="xs" />}
              aria-label={
                line.decision.by_i2q
                  ? `Undo I2Q's decision for ${line.enquiryText}`
                  : `Undo the decision for ${line.enquiryText}`
              }
              onClick={() => writes.decideLine(lineIndex, line.lineKey ?? "", { kind: "clear" })}
            >
              {ACTION_LABELS.undo}
            </Button>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              tone="neutral"
              disabled={writes.busy || !open}
              startIcon={<Icon icon={CircleSlash} size="xs" />}
              aria-label={`No item for ${line.enquiryText}`}
              onClick={() => writes.decideLine(lineIndex, line.lineKey ?? "", { kind: "no_item" })}
            >
              {ACTION_LABELS.noItem}
            </Button>
          )}
          {line.price?.source === "sales" && (
            <Button
              size="sm"
              variant="outline"
              tone="neutral"
              disabled={writes.busy || !open}
              startIcon={<Icon icon={Undo2} size="xs" />}
              aria-label={`Undo the price for ${line.enquiryText}`}
              onClick={() => writes.undoPrice(lineIndex, line.lineKey ?? "")}
            >
              {ACTION_LABELS.undoPrice}
            </Button>
          )}
        </div>
      )}
      <LineFeedback key={line.id} caseId={detail.id} lineIndex={lineIndex} line={line} />
    </div>
  );
}
